import { useEffect, useMemo, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useReplyToInvitationMutation } from '@/state/mutations/invitations/use-reply-to-invitation.ts';
import type { GetInvitationDto } from '@/types/dtos/invitations/response/get-invitation.dto.ts';
import NewEmailVerification from './new-email-verification';
import { organizationRoleLabel } from '@/components/organizations/organization-invitation-role-labels';
import { ORGANIZATION_ROLES, type OrganizationRole } from '@/enums/organization-roles.enum.ts';

function isOrganizationRole(role: string): role is OrganizationRole {
  return (Object.values(ORGANIZATION_ROLES) as string[]).includes(role);
}

type OrganizationInvitationGateProps = {
  invitation: GetInvitationDto;
  invitationKey: string;
  onSuccess: () => void;
};

export default function OrganizationInvitationGate({
  invitation,
  invitationKey,
  onSuccess,
}: OrganizationInvitationGateProps) {
  const { user, isLoaded } = useUser();

  const verifiedMatch = useMemo(() => {
    if (!user || !invitation.inviteeEmail) {
      return false;
    }
    const inviteeNorm = invitation.inviteeEmail.trim().toLowerCase();
    return user.emailAddresses
      .filter((e) => e.verification.status === 'verified')
      .some((e) => e.emailAddress.trim().toLowerCase() === inviteeNorm);
  }, [user, invitation.inviteeEmail]);

  if (!isLoaded) {
    return null;
  }

  if (!verifiedMatch) {
    return (
      <NewEmailVerification
        email={invitation.inviteeEmail}
        onSuccess={async () => {
          await user?.reload();
        }}
      />
    );
  }

  return (
    <OrganizationInvitationAcceptPanel invitation={invitation} invitationKey={invitationKey} onSuccess={onSuccess} />
  );
}

function OrganizationInvitationAcceptPanel({ invitation, invitationKey, onSuccess }: OrganizationInvitationGateProps) {
  const t = useTranslations();
  const notifications = useNotifications();
  const reply = useReplyToInvitationMutation();

  const checklist = useMemo(
    () =>
      invitation.expectedReplies ??
      invitation.roles.map((r) => ({
        entityId: r.entity.id,
        entityType: r.entityType,
        role: r.role,
        inviteeChoiceRequired: true as const,
      })),
    [invitation.expectedReplies, invitation.roles],
  );

  const initialAccept = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const row of checklist) {
      m.set(`${row.entityId}:${row.role}`, true);
    }
    return m;
  }, [checklist]);

  const [choices, setChoices] = useState(() => initialAccept);

  useEffect(() => {
    setChoices(initialAccept);
  }, [initialAccept]);

  const toggle = (entityId: string, role: string, accept: boolean) => {
    setChoices((prev) => {
      const next = new Map(prev);
      next.set(`${entityId}:${role}`, accept);
      return next;
    });
  };

  const handleSubmit = async () => {
    const body = checklist.map((row) => ({
      entityId: row.entityId,
      role: row.role,
      accept: choices.get(`${row.entityId}:${row.role}`) ?? false,
    }));

    try {
      await reply.mutateAsync({ invitationKey, data: body });
      notifications.notifySuccess(t('invitationFlow.success'));
      onSuccess();
    } catch {
      notifications.notifyError(t('invitationFlow.invitationError'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('invitationFlow.organizationAccept.intro')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('invitationFlow.organizationAccept.memberImplied')}
      </Typography>
      <Stack spacing={1}>
        {checklist.map((row) => (
          <FormControlLabel
            key={`${row.entityId}:${row.role}`}
            control={
              <Checkbox
                checked={choices.get(`${row.entityId}:${row.role}`) ?? false}
                onChange={(_, checked) => toggle(row.entityId, row.role, checked)}
                disabled={reply.isPending}
              />
            }
            label={
              isOrganizationRole(row.role) ? organizationRoleLabel(row.role, t) : `${row.entityType} · ${row.role}`
            }
          />
        ))}
      </Stack>
      <Button variant="contained" onClick={() => void handleSubmit()} disabled={reply.isPending}>
        {t('invitationFlow.organizationAccept.submit')}
      </Button>
    </Box>
  );
}
